import React, {
  createElement,
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createRoot } from 'react-dom/client'
import { AutoSearchCon, SearchStyle } from './styles'
import { SEARCH_CONFIG } from '../../Config/config'
import { useAuthContext } from '../../Context'
import { getUserId, logEvent } from '../../Utils'

type AlgoliaSearchModules = {
  autocomplete: any
  createAlgoliaInsightsPlugin: any
  insightsClient: any
  appId: string
  appKey: string
}

export function Autocomplete(props: any) {
  const containerRef = useRef<HTMLDivElement>(null)
  const panelRootRef = useRef<any | null>(null)
  const rootRef = useRef<any | null>(null)
  const [searchModules, setSearchModules] = useState<AlgoliaSearchModules | null>(null)
  const authContext = useAuthContext()

  const userId = getUserId(authContext)

  const setupInsights = useCallback(
    (insightsClient: any) => {
      if (userId) {
        try {
          insightsClient('setUserToken', userId)
        } catch (error) {
          console.error('Failed to set user token for insights:', error)
        }
      }
    },
    [userId]
  )
  
  useEffect(() => {
    const loadSearchModules = async () => {
      try {
        const [autocompleteModule, insightsPlugin, searchInsights, algoliaConfig] =
          await Promise.all([
            import(/* webpackChunkName: "algolia-search" */ '@algolia/autocomplete-js'),
            import(
              /* webpackChunkName: "algolia-search" */ '@algolia/autocomplete-plugin-algolia-insights'
            ),
            import(/* webpackChunkName: "algolia-search" */ 'search-insights'),
            import(/* webpackChunkName: "algolia-search" */ '../../Api/Search/Algolia'),
          ])

        const insightsClient = searchInsights.default
        insightsClient('init', {
          appId: algoliaConfig.appId,
          apiKey: algoliaConfig.appKey,
        })

        setSearchModules({
          autocomplete: autocompleteModule.autocomplete,
          createAlgoliaInsightsPlugin: insightsPlugin.createAlgoliaInsightsPlugin,
          insightsClient,
          appId: algoliaConfig.appId,
          appKey: algoliaConfig.appKey,
        })
      } catch (error) {
        console.warn('Failed to load search modules:', error)
      }
    }

    loadSearchModules()
  }, [])

  useEffect(() => {
    if (!searchModules) return
    setupInsights(searchModules.insightsClient)
  }, [setupInsights, authContext, searchModules])

  useEffect(() => {
    if (!containerRef.current || !searchModules) {
      return undefined
    }

    const { autocomplete, createAlgoliaInsightsPlugin, insightsClient } = searchModules

    const algoliaInsightsPlugin = createAlgoliaInsightsPlugin({
      insightsClient,
      onSelect({ insights, insightsEvents }: any) {
        const events = insightsEvents.map((insightsEvent: any) => {
          switch (insightsEvent.index) {
            case `${SEARCH_CONFIG.pll}`: {
              logEvent('pll_search_click', insightsEvent)
              return {
                ...insightsEvent,
                eventName: `pll_search_click`,
              }
            }
            case 'shopify_products': {
              logEvent('shopify_search_click', insightsEvent)
              return {
                ...insightsEvent,
                eventName: 'shopify_search_click',
              }
            }
            default: {
              return {
                ...insightsEvent,
                eventName: `${insightsEvent.index} Selected`,
              }
            }
          }
        })
        insights.clickedObjectIDsAfterSearch(...events)
      },
    })

    const search = autocomplete({
      container: containerRef.current || '',
      placeholder: 'Search',
      renderer: { createElement, Fragment, render: () => {} },
      render({ children }: any, root: any) {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root

          panelRootRef.current?.unmount()
          panelRootRef.current = createRoot(root)
        }

        panelRootRef.current.render(children)
      },
      plugins: [algoliaInsightsPlugin],
      ...props,
    })

    return () => {
      search.destroy()
    }
  }, [searchModules, props])

  return (
    <AutoSearchCon>
      <SearchStyle ref={containerRef} />
    </AutoSearchCon>
  )
}
